export const COOKIE_NAME = "app_session_id";

// ---------------------------------------------------------------------------
// Order limits
// ---------------------------------------------------------------------------
/** Default minimum coins per order for all users */
export const MIN_COINS_DEFAULT = 500;

/**
 * Per-user custom minimum coins.
 *
 * Each entry is matched by any of: Telegram numeric ID, @username, or email.
 * Lookups are case-insensitive on username/email. If a user matches multiple
 * entries, the SMALLEST minimum wins (most permissive).
 *
 * Keep the admin (aa7mmdd77) at 20 coins and any other privileged accounts
 * at their agreed minimum.
 */
export const CUSTOM_MIN_COINS: Array<{
  min: number;
  identifiers: string[]; // Telegram ID, @username, or email
  /**
   * Optional INDIVIDUAL price per coin (TikTok product). When set, this user
   * pays this price instead of the product's public price, and `min` is used
   * as-is (it is NOT lowered by the product's minQuantity).
   */
  pricePerUnitRub?: number;
}> = [
  {
    min: 20,
    identifiers: [
      "772514834",        // admin aa7mmdd77 (numeric Telegram ID)
      "@aa7mmdd77",       // same admin (username)
      "info@coinsrubot.com", // admin email
    ],
  },
  {
    min: 100,
    identifiers: [
      "@Ahmadaaww", // reseller — custom minimum
    ],
  },
  {
    // Individual terms requested by the owner (2026-09-19): higher price,
    // higher minimum — this account only.
    min: 500,
    pricePerUnitRub: 1.49,
    identifiers: ["anastasiaskuridina492@gmail.com"],
  },
];

/**
 * Find the custom-terms rule for a user (first matching entry), or null.
 * Matches by any of: Telegram numeric ID, @username, or email.
 */
export function findCustomCustomerRule(
  telegramUserId?: string | number | null,
  telegramUsername?: string | null,
  email?: string | null
): { min: number; pricePerUnitRub?: number; identifiers: string[] } | null {
  const id = telegramUserId ? String(telegramUserId) : '';
  const name = telegramUsername
    ? telegramUsername.replace(/^@/, '').trim().toLowerCase()
    : '';
  const mail = (email ?? '').trim().toLowerCase();
  if (!id && !name && !mail) return null;

  for (const entry of CUSTOM_MIN_COINS) {
    const matches = entry.identifiers.some(vip => {
      const v = vip.trim().toLowerCase();
      return v === id || v === name || v === `@${name}` || (mail && v === mail);
    });
    if (matches) return entry;
  }
  return null;
}

/**
 * Returns the minimum coins allowed for a given user.
 * Matches by any of: Telegram numeric ID, @username, or email.
 * Returns MIN_COINS_DEFAULT if no custom entry matches.
 */
export function getMinCoins(
  telegramUserId?: string | number | null,
  telegramUsername?: string | null,
  email?: string | null
): number {
  const id = telegramUserId ? String(telegramUserId) : '';
  const name = telegramUsername
    ? telegramUsername.replace(/^@/, '').trim().toLowerCase()
    : '';
  const mail = (email ?? '').trim().toLowerCase();

  if (!id && !name && !mail) return MIN_COINS_DEFAULT;

  let best = MIN_COINS_DEFAULT;
  for (const entry of CUSTOM_MIN_COINS) {
    const matches = entry.identifiers.some(vip => {
      const v = vip.trim().toLowerCase();
      return v === id ||
        v === name ||
        v === `@${name}` ||
        (mail && v === mail);
    });
    if (matches && entry.min < best) {
      best = entry.min;
    }
  }
  return best;
}

/** Kept for backwards compatibility — the admin's VIP minimum. */
export const MIN_COINS_VIP = 20;

export const ONE_YEAR_MS = 1000 * 60 * 60 * 24 * 365;
export const AXIOS_TIMEOUT_MS = 30_000;
export const UNAUTHED_ERR_MSG = 'Please login (10001)';
export const NOT_ADMIN_ERR_MSG = 'You do not have required permission (10002)';

/**
 * Identifiers that belong to the admin (the owner). Mirrors CUSTOM_MIN_COINS —
 * the admin is @aa7mmdd77 (Telegram id 772514834) / info@coinsrubot.com (the
 * "two entries, same person" case). Edit this list to grant/revoke admin access.
 */
export const ADMIN_IDENTIFIERS = [
  "772514834",          // @aa7mmdd77 — numeric Telegram ID
  "@aa7mmdd77",
  "aa7mmdd77",
  "info@coinsrubot.com",
];

/** True if any of the given identifiers belongs to the admin (case-insensitive, @-agnostic). */
export function isAdminIdentifier(
  telegramId?: string | null,
  telegramUsername?: string | null,
  email?: string | null,
): boolean {
  const norm = (s?: string | null) => (s ? s.trim().toLowerCase().replace(/^@+/, "") : "");
  const id = norm(telegramId);
  const name = norm(telegramUsername);
  const mail = norm(email);
  if (!id && !name && !mail) return false;
  return ADMIN_IDENTIFIERS.some(a => {
    const v = a.trim().toLowerCase().replace(/^@+/, "");
    return (id && v === id) || (name && v === name) || (mail && v === mail);
  });
}

/**
 * Default working-hours window for the TikTok (manual) product, MSK.
 * Editable per-product in the admin panel (products.workingHoursStart/End).
 */
export const DEFAULT_WORKING_HOURS_START = "10:00";
export const DEFAULT_WORKING_HOURS_END = "02:00";

/** Parse "HH:MM" → minutes since midnight. Returns null if invalid. */
function parseHHMM(s?: string | null): number | null {
  if (!s) return null;
  const m = /^(\d{1,2}):(\d{2})$/.exec(s.trim());
  if (!m) return null;
  const h = Number(m[1]);
  const min = Number(m[2]);
  if (h > 23 || min > 59) return null;
  return h * 60 + min;
}

/**
 * True if "now" (MSK) is within the [start, end] working-hours window.
 * - If either bound is missing/invalid → true (24/7, e.g. Steam/Stars).
 * - Handles a window that crosses midnight: end ≤ start means open if
 *   now >= start OR now < end (e.g. 10:00–02:00).
 */
export function isWithinWorkingHours(
  start?: string | null,
  end?: string | null,
  nowMs: number = Date.now(),
): boolean {
  const s = parseHHMM(start);
  const e = parseHHMM(end);
  if (s === null || e === null) return true; // 24/7

  // MSK = UTC+3. Compute current minutes-of-day in MSK.
  const d = new Date(nowMs);
  const mskMinutes = (((d.getUTCHours() + 3) % 24) * 60) + d.getUTCMinutes();

  if (s === e) return true; // open all day
  if (s < e) return mskMinutes >= s && mskMinutes < e; // same-day window
  return mskMinutes >= s || mskMinutes < e; // crosses midnight
}


/**
 * WATA's payment terminal rejects any charge below this (DigitalGoods:ORD_1003
 * "сумма заказа ниже минимальной" — probed live Sep 2026: 150 ₽ passes, 130 ₽
 * fails). Promo discounts are capped so the final charge never drops under it.
 */
export const WATA_MIN_CHARGE_RUB = 150;

/**
 * Ceiling for a single order across TikTok Coins, Telegram Stars and Steam
 * (payment-provider limit). Enforced in the checkout forms AND on the server, so
 * it also holds if a price per coin/star or the Steam markup changes.
 */
export const MAX_ORDER_AMOUNT_RUB = 19_000;

/**
 * Telegram Stars: acquiring fee the buyer covers when they pay by BANK CARD
 * instead of СБП. The base price stays for СБП (no fee); choosing the card adds
 * this percent on top of the amount we charge through Т-Банк. Applied on the
 * server AFTER the promo discount, so orders.priceRub always matches the amount
 * the bank actually charges (the webhook verifies exactly that).
 */
export const CARD_SURCHARGE_PERCENT = 2;

/**
 * Cashback earned on a completed TikTok order — percent of the order's COIN
 * COUNT (not of the ruble amount), credited to the balance as bonus coins:
 * 100 coins → 1 coin, 1 000 → 10, 5 000 → 50.
 *
 * ⛔ LOCKED BY THE OWNER (Brian): 1%. Single source of truth for the awarded
 * coins (api cashback.ts) and the checkout summary row.
 */
export const CASHBACK_PERCENT = 1;

/**
 * Referral bonus — percent of the invitee's coins that the referrer earns.
 *
 * ⛔ SET BY THE OWNER (Brian), 2026-10-10: **3%** (was 1%), and it is paid ONLY
 * for the invitee's first TikTok-coins order — Stars, Steam and digital-goods
 * orders never trigger it. UI texts must render this constant; never hardcode it.
 */
export const REFERRAL_BONUS_PERCENT = 3;
